import { useCallback, type MutableRefObject } from "react";
import { useForm } from "@core/features/form";
import {
  useProfileMutation,
  normalizeUserData,
  RawUserData,
} from "@core/features/profile";
import { usePlatformStore } from "@platform/store";
import {
  useStepFormSync,
  type StepValidationState,
} from "@platform/features/onboarding";
import { User } from "@platform/types/platform.types";
import { ProfileForm } from "./ProfileForm";

export type Props = {
  profileData: RawUserData;
  userRole: string;
  /** Optional callback when profile is saved successfully */
  onSaveSuccess?: () => void;
  /** Whether to show the cancel button (defaults to true when form is dirty) */
  showCancelButton?: boolean;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Additional CSS classes for the container */
  className?: string;
};

/**
 * Inner form component that only mounts once profile data is available.
 * This ensures useProfileMutation's useState captures the correct initial values.
 */
export function ProfileFormContent({
  profileData,
  userRole,
  onSaveSuccess,
  showCancelButton,
  primaryButtonText = "Save Profile",
  showActionButtons = true,
  onStepStateChange,
  submitRef,
  className,
}: Props) {
  const setUser = usePlatformStore((s) => s.setUser);
  const normalizedUser = normalizeUserData(profileData as RawUserData);

  const { schema, defaultValues, ...mutation } = useProfileMutation({
    userRole,
    previousValues: normalizedUser,
    onUserUpdate: (userData) => {
      setUser(userData as User);
    },
  });

  const { form, handleSubmit } = useForm({
    schema,
    onSubmit: async (data) => {
      await mutation.mutateAsync(data);
      onSaveSuccess?.();
    },
    defaultValues,
  });

  const handleSubmitStable = useCallback(() => {
    return handleSubmit();
  }, [handleSubmit]);

  const handleCancel = () => {
    form.reset();
  };

  const { isSubmitting, isValid, isDirty } = form.formState;

  useStepFormSync({
    shouldSync: !showActionButtons,
    onStepStateChange,
    submitRef,
    formState: { isValid, isDirty, isSubmitting },
    onSubmit: handleSubmitStable,
  });

  return (
    <div className={className}>
      <ProfileForm
        form={form}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        showCancelButton={showCancelButton}
        primaryButtonText={primaryButtonText}
        showActionButtons={showActionButtons}
      />
    </div>
  );
}
